import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const contactInfo = [
    {
      icon: Mail,
      labelKey: 'email',
      value: 'loomagency55@gmail.com',
      href: 'mailto:loomagency55@gmail.com',
    },
    {
      icon: Phone,
      labelKey: 'phone',
      value: '+351 926 132 780',
      href: 'tel:+351926132780',
    },
    {
      icon: Instagram,
      labelKey: 'instagram',
      value: '@agency.loom',
      href: 'https://instagram.com/agency.loom',
    },
    {
      icon: MapPin,
      labelKey: 'location',
      value: 'Tavira, Portugal',
      href: 'https://maps.google.com/?q=Tavira,Portugal',
    },
  ];

  return (
    <section 
      id="contacto" 
      data-testid="contact-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 bg-[#eaddcf]/30"
    >
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Content */}
          <div className="text-center md:text-left">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs md:text-sm font-medium uppercase tracking-widest text-[#ff00c7] mb-4 block"
            >
              {t.contact.badge}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="contact-title"
              className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#242328] leading-tight mb-6"
            >
              {t.contact.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-[#242328]/70 leading-relaxed mb-10"
            >
              {t.contact.description}
            </motion.p>

            {/* Contact info */}
            <div className="space-y-4">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={item.labelKey}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  data-testid={`contact-${item.labelKey}`}
                  className="group flex items-center gap-4 p-4 rounded-xl hover:bg-white/50 transition-colors duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#242328] flex items-center justify-center group-hover:bg-[#ff00c7] transition-colors duration-300">
                    <item.icon className="w-5 h-5 text-[#f8f6e2]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#242328]/50 mb-1">
                      {t.contact.labels[item.labelKey]}
                    </p>
                    <p className="font-medium text-[#242328] group-hover:text-[#ff00c7] transition-colors duration-300">
                      {item.value}
                    </p>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Right - Image/Map placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="aspect-square rounded-2xl overflow-hidden bg-[#242328]/5">
              <img
                src="https://customer-assets.emergentagent.com/job_4ea5ffca-c37f-4c05-ba81-583edc3692b4/artifacts/awlzz6mg_WhatsApp%20Image%202026-03-19%20at%2020.14.24.jpeg"
                alt="Loom Agency creative space"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#242328]/30 to-transparent" />
            </div>
            
            {/* Location badge */}
            <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-xl px-6 py-4 shadow-lg">
              <p className="font-['Syne'] text-sm font-semibold text-[#242328]">Tavira, Portugal</p>
              <p className="text-xs text-[#242328]/60">{t.contact.locationBadge}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
